import {
  CreateGroupInputs,
  GetGroupsResponse,
  Group,
  GroupsFilters,
  VocabularyWord,
  UpdateVocabularyGroupInputs,
  AddWordToGroupInputs,
} from "./vocabularyGroups.interfaces";
import { mockIds } from "../mockIds";
import { delay, generateUID } from "@/lib/utils";

const mockGroups: Group[] = [
    {
        id: mockIds.group1,
        name: "Vocabulário Básico",
        description: "Palavras e expressões básicas para iniciantes",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        words: []
    },
    {
        id: mockIds.group2,
        name: "Expressões Cotidianas",
        description: "Expressões comuns usadas no dia a dia",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        words: []
    },
    {
        id: mockIds.group3,
        name: "Vocabulário Avançado",
        description: "Palavras e expressões para níveis avançados",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        words: []
    }
];

export const getGroups = async (
  filters: GroupsFilters
): Promise<GetGroupsResponse> => {
  await delay(1000);

  let filteredGroups = [...mockGroups];

  // Aplicar filtro de busca
  if (filters.search) {
    const searchLower = filters.search.toLowerCase();
    filteredGroups = filteredGroups.filter(
      (group) =>
        group.name.toLowerCase().includes(searchLower) ||
        group.description?.toLowerCase().includes(searchLower)
    );
  }

  // Calcular paginação
  const page = filters.page || 1;
  const pageSize = filters.pageSize || 10;
  const start = (page - 1) * pageSize;
  const end = start + pageSize;

  return {
    data: filteredGroups.slice(start, end),
    total: filteredGroups.length,
    page,
    pageSize,
  };
};

export const getGroup = async (id: string): Promise<Group> => {
  await delay(1000);
  const group = mockGroups.find((g) => g.id === id);
  if (!group) {
    throw new Error("Grupo não encontrado");
  }
  return group;
};

export const getGroupDetails = async (id: string): Promise<Group> => {
  await delay(1000);
    const group = mockGroups.find(g => g.id === id);
    if (!group) {
        throw new Error('Grupo não encontrado');
    }
    return {
        ...group,
        words: group.words || [] // Garantir que sempre retorne um array, mesmo que vazio
    };
};

export const createGroup = async (
  data: CreateGroupInputs
): Promise<string> => {
  await delay(1000);
  const newId = generateUID();

  const newGroup: Group = {
    id: newId,
    name: data.name,
    description: data.description,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    words: [],
  };

  mockGroups.push(newGroup);
  return newId;
};

export const updateGroup = async (
  id: string,
  data: UpdateVocabularyGroupInputs
): Promise<void> => {
  await delay(1000);
  const index = mockGroups.findIndex((g) => g.id === id);
  if (index === -1) {
    throw new Error("Grupo não encontrado");
  }

  mockGroups[index] = {
    ...mockGroups[index],
    ...data,
    updatedAt: new Date().toISOString(),
  };
};

export const deleteGroup = async (ids: string[]): Promise<void> => {
  await delay(1000);
  const indexesToDelete = mockGroups
    .map((group, index) => ids.includes(group.id) ? index : -1)
    .filter((index) => index !== -1);

  if (indexesToDelete.length === 0) {
    throw new Error("Nenhum grupo encontrado para exclusão");
  }

  indexesToDelete.reverse().forEach((index) => {
    mockGroups.splice(index, 1);
  });
};

export const addWordToGroup = async (
  groupId: string,
  wordData: AddWordToGroupInputs
): Promise<void> => {
  await delay(1000);
  const groupIndex = mockGroups.findIndex((g) => g.id === groupId);
  if (groupIndex === -1) {
    throw new Error("Grupo não encontrado");
  }

  if (!mockGroups[groupIndex].words) {
    mockGroups[groupIndex].words = [];
  }

  const newWord: VocabularyWord = {
    id: generateUID(),
    word: wordData.word,
    translation: wordData.translation,
    exampleSentence: wordData.exampleSentence
  }

  mockGroups[groupIndex].words.push(newWord);
};

export const removeWordFromGroup = async (
  groupId: string,
  wordId: string
): Promise<void> => {
  await delay(1000);
  const groupIndex = mockGroups.findIndex((g) => g.id === groupId);
  if (groupIndex === -1) {
    throw new Error("Grupo não encontrado");
  }

  if (!mockGroups[groupIndex].words) {
    mockGroups[groupIndex].words = [];
  }

  const wordIndex = mockGroups[groupIndex].words.findIndex(w => w.id === wordId);
  if (wordIndex > -1) {
    mockGroups[groupIndex].words.splice(wordIndex, 1);
  }
};

export const updateWordInGroup = async (
    groupId: string,
    wordId: string,
    word: VocabularyWord
): Promise<void> => {
    await delay(1000);
    const groupIndex = mockGroups.findIndex(g => g.id === groupId);

    if (groupIndex === -1) {
        throw new Error('Grupo não encontrado');
    }

    const wordIndex = mockGroups[groupIndex].words.findIndex(w => w.id === wordId);

    if (wordIndex === -1) {
        throw new Error('Palavra não encontrada');
    }

    mockGroups[groupIndex].words[wordIndex] = {
        ...mockGroups[groupIndex].words[wordIndex],
        ...word
    };
}